import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import DataTableOperations from '@blakeelearning/data-tables/components/data-table-operations'
import type ReStudentSummaryStoriesWrittenRoute from 'teacher-ui-client/reporting/ui-scope/re/student-summary/stories-written/route'
import { t, type IntlService } from 'ember-intl'
import { array, fn } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { not } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { CalendarIcon } from 'teacher-ui-client/components/calendar-icon'
import RouteTemplate from 'ember-route-template'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { unixTimeToCalendarIconArgs } from 'teacher-ui-client/utils/unix-time-to-calendar-icon'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'

interface Signature {
  Args: {
    model: ModelFor<ReStudentSummaryStoriesWrittenRoute>
  }
}
export class ReStudentSummaryStoriesWrittenPage extends Component<Signature> {
  @service intl!: IntlService

  @tracked currentSortColumn = 'created_at'

  get studentName() {
    const studentName = this.args.model.student.fullName
    return studentName ?? ''
  }

  get mappedStoriesWritten() {
    const records = this.args.model.storiesWrittenDetails ?? []
    return records.map((record) => {
      const url = join(
        config.readingEggsBaseUrl,
        '/students/',
        this.args.model.student.id,
        '/story_factory_entries/',
        `${record.story_id}.pdf`,
      ).href
      const calendarCell = unixTimeToCalendarIconArgs(record.created_at, this.intl)
      return { ...record, url, calendarCell }
    })
  }

  <template>
    <ReportingTeacherUiHeader
      @title={{t "reporting.title.studentStoriesWritten" studentName=this.studentName}}
      @showBack={{true}}
    />

    <ReportingDetailedControls
      @students={{@model.students}}
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{Boolean @model.storiesWrittenDetails}}
      @showDatePicker={{true}}
      @studentRouteName="reporting.ui-scope.re.student-summary.stories-written"
    />

    <ReportingNoDataMessageIf @on={{not @model.storiesWrittenDetails}}>
      <DataTableOperations @data={{this.mappedStoriesWritten}} as |transformedData tableAction|>
        <ContentTile data-test-stories-written-student-details-panel>
          <h3 class="border-oceany-blue-150 border-b-4 p-4 print:border-black">{{t
              "reporting.re.storiesWritten.table.title"
            }}</h3>
          <table class="w-full">
            <thead>
              <tr class="bg-oceany-blue-100 border-b border-white">
                <th
                  {{on "click" (fn tableAction "sortByNumber" "created_at")}}
                  role="button"
                  class="p-4 text-black hover:text-black"
                >
                  <span>
                    {{t "reporting.re.storiesWritten.table.headers.createdAt"}}
                    <PhosphorIcon role="presentation" @icon="phosphor-caret-up-down-fill" />
                  </span>
                </th>
                <th
                  {{on "click" (fn tableAction "sortByValue" "title")}}
                  role="button"
                  class="p-4 text-black hover:text-black"
                >
                  <span>
                    {{t "reporting.re.storiesWritten.table.headers.storyTitle"}}
                    <PhosphorIcon role="presentation" @icon="phosphor-caret-up-down-fill" />
                  </span>
                </th>
                <th
                  {{on "click" (fn tableAction "sortByValue" "contest_name")}}
                  role="button"
                  class="p-4 text-black hover:text-black"
                >
                  <span>
                    {{t "reporting.re.storiesWritten.table.headers.contestName"}}
                    <PhosphorIcon role="presentation" @icon="phosphor-caret-up-down-fill" />
                  </span>
                </th>
                <th class="print:hidden"></th>
              </tr>
            </thead>
            <tbody>
              {{#each transformedData as |row index|}}
                <tr
                  data-test-stories-written-student-row={{index}}
                  class="even:bg-oceany-blue-50 hover:bg-oceany-blue-100 bg-white"
                >
                  <td class="p-4">
                    <CalendarIcon
                      class="text-oceany-blue-300"
                      @month={{row.calendarCell.value.month}}
                      @day={{row.calendarCell.value.day}}
                    />
                  </td>
                  <td class="p-4">
                    {{row.title}}
                  </td>
                  <td class="p-4">
                    {{row.contest_name}}
                  </td>
                  <td class="p-4 print:hidden">
                    <a href={{row.url}} target="blank" class="text-xl">
                      <PhosphorIcon @icon="phosphor-download-fill" />
                      <span>
                        {{t "reporting.re.storiesWritten.table.download"}}
                      </span>
                    </a>
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </ContentTile>
      </DataTableOperations>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(ReStudentSummaryStoriesWrittenPage)
